import { emptyFilter, type DataSource, type ListQuery } from "@estelle/domain";

import {
  CHANGE_FIELDS,
  ESCALATION_FIELDS,
  ORDER_FIELD_PERMISSIONS,
  ORDER_FIELDS,
  RETURN_FIELDS,
  SHIPMENT_FIELDS,
} from "@/modules/orders/fields";
import { collectAll } from "@/components/table/export";
import { ChangesList } from "@/modules/orders/screens/ChangesList";
import { EscalationsList } from "@/modules/orders/screens/EscalationsList";
import { projectOrder, projectReturnRow } from "@/modules/orders/projections";
import { ordersList } from "@/modules/orders/screens/OrdersList";
import { ReturnsList } from "@/modules/orders/screens/ReturnsList";
import { ShipmentsList } from "@/modules/orders/screens/ShipmentsList";
import {
  UNFULFILLED,
  withStanding,
  type StandingCondition,
} from "@/modules/orders/standing";
import type { ModuleUi } from "@/modules/types";

/**
 * An orders section's export: the rows its list shows.
 *
 * The standing condition goes through `withStanding`, the function the list
 * calls. An export that left it out was every order under a screen that showed
 * the unfulfilled ones.
 */
function exportOrders(standing?: StandingCondition) {
  return (data: DataSource, query: ListQuery) =>
    collectAll(
      (page) => data.orders.list(page),
      {
        ...query,
        filter: withStanding(standing, query.filter ?? emptyFilter()),
      },
      projectOrder,
    );
}

/**
 * How many orders a section holds under a query (#633): one row asked for,
 * the total read off the page, under the same standing filter as the list.
 */
function countOrders(standing?: StandingCondition) {
  return async (data: DataSource, query: ListQuery) =>
    (
      await data.orders.list({
        ...query,
        filter: withStanding(standing, query.filter ?? emptyFilter()),
        limit: 1,
      })
    ).total;
}

/** What Orders is usually narrowed by (#633), as chips under the search. */
const ORDER_COMMON_FILTERS = [
  "stage",
  "financialStatus",
  "sourceApp",
  "placedAt",
];

/**
 * The Orders module.
 *
 * `all` and `unfulfilled` are the same list under a different standing
 * filter, which is the shape the shared list framework was built for — a
 * section is a query, not a screen — and share `ORDER_FIELDS` because both
 * render orders. `shipments` and `changes` render a different row shape
 * (a shipment, a change event) from their own repository lists, so each
 * declares its own field vocabulary rather than reusing the order one.
 */
export const ordersModule: ModuleUi = {
  id: "orders",
  sections: {
    all: {
      fields: ORDER_FIELDS,
      fieldPermissions: ORDER_FIELD_PERMISSIONS,
      screen: ordersList(),
      exportRows: exportOrders(),
      commonFilters: ORDER_COMMON_FILTERS,
      count: countOrders(),
    },
    unfulfilled: {
      fields: ORDER_FIELDS,
      fieldPermissions: ORDER_FIELD_PERMISSIONS,
      screen: ordersList(UNFULFILLED),
      exportRows: exportOrders(UNFULFILLED),
      commonFilters: ORDER_COMMON_FILTERS,
      count: countOrders(UNFULFILLED),
    },
    shipments: {
      fields: SHIPMENT_FIELDS,
      screen: ShipmentsList,
      commonFilters: ["stage", "courier", "health", "shippedAt"],
      count: async (data, query) =>
        (await data.orders.listShipments({ ...query, limit: 1 })).total,
    },
    /*
      Returns to receive (#529): failed and returning shipments nobody has
      received. A shipment's own row type, so its own fields; the receiving
      screen opens over it.
    */
    returns: {
      fields: RETURN_FIELDS,
      subject: "returns",
      screen: ReturnsList,
      exportRows: (data, query) =>
        collectAll(
          (page) => data.returns.listToReceive(page),
          query,
          projectReturnRow,
        ),
    },
    /*
      Delivery escalations (#702): parcels that failed delivery, went quiet or
      were lost, one row per shipment and kind. Its tabs are standing filters
      the screen ANDs with the person's own. The bulk Export CSV on the
      selection bar is its export: a toolbar export would have no tab to say
      which status it holds.
    */
    escalations: {
      fields: ESCALATION_FIELDS,
      subject: "escalations",
      screen: EscalationsList,
      nothingYet: { title: "No delivery escalations" },
      commonFilters: ["kind", "courier", "payment", "daysOpen"],
      count: async (data) => (await data.escalations.counts()).new,
    },
    changes: {
      fields: CHANGE_FIELDS,
      screen: ChangesList,
      nothingYet: { title: "No order changes yet" },
    },
  },
};
